import { useEffect, useRef, useState } from 'react';
import { loadDojoSDK } from '../dojo';

const CONTAINER_ID = 'checkout-dojo-card';
const WALLET_CONTAINER_ID = 'checkout-dojo-wallet';

// displayErrors may hand us an array, a single value, an object or nothing.
function normaliseErrors(errors) {
  if (!errors) return [];
  const list = Array.isArray(errors) ? errors : [errors];
  return list
    .map((e) => (typeof e === 'string' ? e : e && typeof e.message === 'string' ? e.message : null))
    .filter(Boolean);
}

// executePayment's statusCode: 0 = paid. Only a code known to mean the card was NOT
// charged is shown as a decline (the same intent is then renewed). Anything else —
// authorising (3), duplicate (20), a gateway error (500), a code we do not know, no
// result at all — may have taken money, so it goes to confirm and the server asks
// Dojo. Codes per Dojo's SDK docs (4 referred, 5 declined, 30 failed, 400/401/404
// request or token errors), confirmed against Dojo's published status codes and the
// sandbox (decline = 5, success = 0, 3DS success = 0). This component exists in
// three copies (booking widget, pay-app, online ordering) — keep them in step.
const NOT_CHARGED_STATUS_CODES = new Set([4, 5, 30, 400, 401, 404]);

// Dojo's embedded card form. onPayStart() fires just before executePayment. onError(message, retryable): retryable means the
// session should be refreshed and the form remounted (the promise it returns keeps
// Pay disabled until that has finished). wallet adds Dojo's Apple Pay / Google Pay
// button above the card form (hidden ?applepay=1 trial only; the intent must have been
// created with Wallet allowed). Its errors are shown on screen so the trial can be read
// on a phone.
export default function DojoCardPayment({ clientSessionSecret, amountLabel, onPayStart, onPaid, onError, wallet = false }) {
  const cardRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const inFlightRef = useRef(false);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [walletErrors, setWalletErrors] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setFieldErrors([]);
    loadDojoSDK().then((Dojo) => {
      if (cancelled) return;
      cardRef.current = new Dojo.Payment({
        paymentDetails: { paymentToken: clientSessionSecret },
        containerId: CONTAINER_ID,
        styles: { base: { default: { color: '#111827', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" } } },
        onIframeLoaded: () => { if (!cancelled) setReady(true); },
        onIframeLoadFailed: () => { if (!cancelled) onError('Card payment form failed to load.'); },
      }, (errors) => { if (!cancelled) setFieldErrors(normaliseErrors(errors)); });
    }).catch((e) => { if (!cancelled) onError(e.message); });
    return () => {
      cancelled = true;
      // Dispose the old instance if the SDK offers a way to.
      const old = cardRef.current;
      for (const m of ['destroy', 'unmount', 'dispose']) {
        if (old && typeof old[m] === 'function') { try { old[m](); } catch { /* ignore */ } break; }
      }
      cardRef.current = null;
    };
  }, [clientSessionSecret]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!wallet) return undefined;
    let cancelled = false;
    let walletPayment = null;
    setWalletErrors([]);
    loadDojoSDK().then((Dojo) => {
      if (cancelled) return;
      if (typeof Dojo.WalletPayment !== 'function') { setWalletErrors(['Dojo wallet component not available.']); return; }
      walletPayment = new Dojo.WalletPayment({
        paymentDetails: { paymentToken: clientSessionSecret },
        containerId: WALLET_CONTAINER_ID,
        buttonConfig: { color: 'black', type: 'plain' },
        emailRequired: false,
        billingAddressRequired: false,
        shippingAddressRequired: false,
      }, (errors) => {
        console.warn('[checkout] Dojo wallet errors', errors);
        if (!cancelled) setWalletErrors(normaliseErrors(errors));
      }, (res) => {
        console.info('[checkout] Dojo wallet result', res);
        if (cancelled) return;
        // The wallet sheet gives no "about to charge" moment, so the attempt is marked
        // possibly charged when its result arrives.
        if (onPayStart) onPayStart();
        handleResult(res);
      });
    }).catch((e) => { if (!cancelled) setWalletErrors([e.message]); });
    return () => {
      cancelled = true;
      for (const m of ['destroy', 'unmount', 'dispose']) {
        if (walletPayment && typeof walletPayment[m] === 'function') { try { walletPayment[m](); } catch { /* ignore */ } break; }
      }
    };
  }, [clientSessionSecret, wallet]); // eslint-disable-line react-hooks/exhaustive-deps

  // A result from either the card form or the wallet button.
  async function handleResult(res) {
    if (res && NOT_CHARGED_STATUS_CODES.has(res.statusCode)) {
      // Stay busy until the parent has refreshed the session (it remounts us).
      // Dojo's result message is not a reliable reason (a plain decline can read "Failed Payer
      // Authentication"), so the customer is told only that the card was declined.
      await onError('Payment declined.', true);
    } else {
      await onPaid(res || {});
    }
  }

  async function pay() {
    if (!cardRef.current || inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy(true);
    // Tell the parent before any money can move (it records the attempt as possibly charged).
    if (onPayStart) onPayStart();
    try {
      await handleResult(await cardRef.current.executePayment());
    } catch (e) {
      if (Array.isArray(e)) {
        // Rejected before submission with validation errors: nothing was sent, so
        // keep the form (and what the customer typed) and show the messages.
        setFieldErrors(normaliseErrors(e));
      } else if (e && typeof e.statusCode === 'number' && !NOT_CHARGED_STATUS_CODES.has(e.statusCode)) {
        // Rejected with a result code that may have taken money (e.g. 500): confirm.
        onPaid(e);
      } else {
        await onError(typeof e === 'string' ? e : (e && e.message) || 'Payment failed.', true);
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  }

  return (
    <div>
      {wallet && <div key={`w-${clientSessionSecret}`} id={WALLET_CONTAINER_ID} style={{ marginBottom: 12 }} />}
      {walletErrors.length > 0 && (
        <ul className="checkout-payment-error" role="list">{walletErrors.map((m, i) => <li key={i}>{m}</li>)}</ul>
      )}
      <div key={clientSessionSecret} id={CONTAINER_ID} className="checkout-card-iframe" />
      {fieldErrors.length > 0 && (
        <ul className="checkout-payment-error" role="list">{fieldErrors.map((m, i) => <li key={i}>{m}</li>)}</ul>
      )}
      <button type="button" className="checkout-place-order-btn" style={{ marginTop: 12 }} disabled={!ready || busy} onClick={pay}>
        {busy ? 'Processing…' : (amountLabel ? `Pay ${amountLabel}` : 'Pay')}
      </button>
    </div>
  );
}
